import { useRef, useEffect, useState, type ReactNode } from 'react';

type AnimationType = 'fade-up' | 'fade-down' | 'fade-left' | 'fade-right' | 'zoom-in' | 'zoom-out' | 'flip-up' | 'fade';

interface ScrollRevealWrapperProps {
  children: ReactNode;
  animation?: AnimationType;
  delay?: number;
  duration?: number;
  threshold?: number;
  className?: string;
  stagger?: boolean;
  staggerDelay?: number;
}

const getInitialStyles = (animation: AnimationType): React.CSSProperties => {
  const base: React.CSSProperties = { opacity: 0 };
  switch (animation) {
    case 'fade-up':
      return { ...base, transform: 'translateY(60px)' };
    case 'fade-down':
      return { ...base, transform: 'translateY(-60px)' };
    case 'fade-left':
      return { ...base, transform: 'translateX(-60px)' };
    case 'fade-right':
      return { ...base, transform: 'translateX(60px)' };
    case 'zoom-in':
      return { ...base, transform: 'scale(0.85)' };
    case 'zoom-out':
      return { ...base, transform: 'scale(1.15)' };
    case 'flip-up':
      return { ...base, transform: 'perspective(800px) rotateX(15deg) translateY(40px)' };
    case 'fade':
    default:
      return base;
  }
};

const getVisibleStyles = (): React.CSSProperties => ({
  opacity: 1,
  transform: 'translateY(0) translateX(0) scale(1) rotateX(0deg)',
});

export function ScrollRevealWrapper({
  children,
  animation = 'fade-up',
  delay = 0,
  duration = 700,
  threshold = 0.12,
  className = '',
  stagger = false,
  staggerDelay = 120,
}: ScrollRevealWrapperProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold, rootMargin: '0px 0px -60px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  useEffect(() => {
    if (!stagger || !isVisible || !ref.current) return;

    const children = ref.current.querySelectorAll('[data-stagger-child]');
    children.forEach((child, i) => {
      const el = child as HTMLElement;
      el.style.transition = `opacity ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay + i * staggerDelay}ms, transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay + i * staggerDelay}ms`;
      el.style.opacity = '1';
      el.style.transform = 'translateY(0) translateX(0) scale(1)';
    });
  }, [isVisible, stagger, staggerDelay, delay, duration]);

  const style: React.CSSProperties = {
    ...(isVisible ? getVisibleStyles() : getInitialStyles(animation)),
    transition: `opacity ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
    willChange: 'opacity, transform',
  };

  return (
    <div ref={ref} style={style} className={className}>
      {children}
    </div>
  );
}

export function StaggerChild({
  children,
  animation = 'fade-up',
  className = '',
}: {
  children: ReactNode;
  animation?: AnimationType;
  className?: string;
}) {
  return (
    <div
      data-stagger-child
      className={className}
      style={{ ...getInitialStyles(animation) }}
    >
      {children}
    </div>
  );
}

export default ScrollRevealWrapper;
